interface PendingQuestion {
  /** Identifies the question, so the same one asked twice shares one dialog. */
  key: string
  promise: Promise<boolean>
  settle: (confirmed: boolean) => void
}

/**
 * A single dialog slot shared by every caller in the tool.
 *
 * Two callers can race for it - a page leave guard and an open slideover both reacting to the
 * same click. Asking the question already on screen hands back the answer being waited for, so
 * one dialog settles both; only a genuinely different question takes the slot away, and the
 * caller it displaces gets the safe answer rather than a silent success.
 *
 * `show` renders the question, or dismisses the dialog when passed `null`.
 */
export function createConfirmSlot<TOptions>(show: (options: TOptions | null) => void) {
  let pending: PendingQuestion | null = null

  return {
    ask(options: TOptions): Promise<boolean> {
      const key = JSON.stringify(options)

      if (pending?.key === key) {
        return pending.promise
      }

      pending?.settle(false)

      let settle!: (confirmed: boolean) => void
      const promise = new Promise<boolean>((resolve) => {
        settle = resolve
      })

      pending = { key, promise, settle }
      show(options)

      return promise
    },

    answer(confirmed: boolean) {
      const answered = pending

      pending = null
      show(null)
      answered?.settle(confirmed)
    },
  }
}
